import { FC } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid2 as Grid,
  Divider,
  Card,
  CardContent,
  CardHeader,
  List,
  CircularProgress,
  Stack,
  Avatar,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  School as SchoolIcon,
  Work as WorkIcon,
  ContactPhone as ContactPhoneIcon,
  Group as GroupIcon,
  CardMembership as CardMembershipIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

import {
  JobseekerExperience,
  JobseekerEducation,
  JobseekerReference,
  JobseekerAssociation,
  JobseekerCertificate,
} from '@libs/models';
import { DashboardPageContainer, DownloadResumeButton, VideosViewCard } from '@carcareers/components';
import { useJobseekerViewQuery } from '@carcareers/store';
import { useSnackbar } from '@carcareers/contexts';
import { useIsMobile } from '@carcareers/utils';
import { formatPhone } from '@libs/utils';

export const EmployerJobseekerView: FC = () => {
  const { toggleSnackbar } = useSnackbar();

  const { id } = useParams();
  const jobseekerId = id ? parseInt(id) : undefined;
  const isMobile = useIsMobile();

  const { data: jobseeker, isLoading, error } = useJobseekerViewQuery(jobseekerId ?? 0, { skip: !jobseekerId });

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  if (error) {
    toggleSnackbar('error', 'Error loading jobseeker information. Please try again later.');
  }

  if (isLoading || !jobseeker) {
    return (
      <DashboardPageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </DashboardPageContainer>
    );
  }

  return (
    <DashboardPageContainer idReplacement={`${jobseeker.user?.firstName} ${jobseeker.user?.lastName}`}>
      <Stack spacing={3}>
        {/* Header Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {isLoading ? '' : getInitials(jobseeker.user?.firstName, jobseeker.user?.lastName)}
              </Avatar>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isLoading ? (
                    <Skeleton variant="text" width={300} height={32} />
                  ) : (
                    `${jobseeker.user?.firstName} ${jobseeker.user?.lastName}`
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" fontSize="small" />
                    <Typography variant="body1">{jobseeker.user?.email}</Typography>
                  </Box>

                  {jobseeker.user?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="primary" fontSize="small" />
                      <Typography variant="body1">{formatPhone(jobseeker.user?.phone)}</Typography>
                    </Box>
                  )}
                </Box>

                {jobseeker.resume && (
                  <Box>
                    <DownloadResumeButton jobseeker={jobseeker} showViewButton />
                  </Box>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        {jobseeker.isExternalApply && (
          <Alert variant="outlined" severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
            <Typography variant="body1" color="warning.dark">
              {jobseeker.user?.firstName} has applied to this job through one of our partners. Invite them to
              CARCAREERS.COM or contact them directly for more information.
            </Typography>
          </Alert>
        )}
        <VideosViewCard jobseekerId={jobseeker.id} />
        {/* Experience and Education */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              elevation={0}
              sx={{ height: '100%', borderRadius: 3, elevation: 0, backgroundColor: 'background.paper' }}
            >
              <CardHeader
                title="Work Experience"
                avatar={<WorkIcon sx={{ color: 'primary.contrastText' }} />}
                sx={{
                  backgroundColor: 'primary.main',
                  '& .MuiCardHeader-title': { color: 'primary.contrastText' },
                }}
              />
              <CardContent>
                {jobseeker.experiences && jobseeker.experiences.length > 0 ? (
                  <List>
                    {jobseeker.experiences.map((exp: JobseekerExperience) => (
                      <Box key={exp.id} sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h3">
                          {exp.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          {exp.company}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {`${exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'N/A'} - ${
                            exp.current ? 'Present' : exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'N/A'
                          }`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {exp.description}
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No experience information available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          {/* Education */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              elevation={0}
              sx={{ height: '100%', borderRadius: 3, elevation: 0, backgroundColor: 'background.paper' }}
            >
              <CardHeader
                title="Education"
                avatar={<SchoolIcon sx={{ color: 'primary.contrastText' }} />}
                sx={{
                  backgroundColor: 'primary.main',
                  '& .MuiCardHeader-title': { color: 'primary.contrastText' },
                }}
              />
              <CardContent>
                {jobseeker.educations && jobseeker.educations.length > 0 ? (
                  <List>
                    {jobseeker.educations.map((edu: JobseekerEducation) => (
                      <Box key={edu.id} sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h3">
                          {edu.level}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          {edu.school}, {edu.areaOfStudy}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {`${edu.startDate ? new Date(edu.startDate).toLocaleDateString() : 'N/A'} - ${
                            edu.current ? 'Present' : edu.endDate ? new Date(edu.endDate).toLocaleDateString() : 'N/A'
                          }`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {edu.description}
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No education information available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Certificates */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              elevation={0}
              sx={{ height: '100%', borderRadius: 3, elevation: 0, backgroundColor: 'background.paper' }}
            >
              <CardHeader
                title="Certificates"
                avatar={<CardMembershipIcon sx={{ color: 'primary.contrastText' }} />}
                sx={{
                  backgroundColor: 'primary.main',
                  '& .MuiCardHeader-title': { color: 'primary.contrastText' },
                }}
              />
              <CardContent>
                {jobseeker.certificates && jobseeker.certificates.length > 0 ? (
                  <List>
                    {jobseeker.certificates.map((cert: JobseekerCertificate) => (
                      <Box key={cert.id} sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h3">
                          {cert.name}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          {cert.issuingAuthority}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Issued: {cert.issueDate ? new Date(cert.issueDate).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {cert.description}
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No certificate information available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Associations */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              elevation={0}
              sx={{ height: '100%', borderRadius: 3, elevation: 0, backgroundColor: 'background.paper' }}
            >
              <CardHeader
                title="Associations"
                avatar={<GroupIcon sx={{ color: 'primary.contrastText' }} />}
                sx={{
                  backgroundColor: 'primary.main',
                  '& .MuiCardHeader-title': { color: 'primary.contrastText' },
                }}
              />
              <CardContent>
                {jobseeker.associations && jobseeker.associations.length > 0 ? (
                  <List>
                    {jobseeker.associations.map((association: JobseekerAssociation) => (
                      <Box key={association.id} sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h3">
                          {association.name}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {association.description}
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No association information available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* References */}
        <Card elevation={0} sx={{ borderRadius: 3, elevation: 0, backgroundColor: 'background.paper' }}>
          <CardHeader
            title="References"
            avatar={<ContactPhoneIcon sx={{ color: 'primary.contrastText' }} />}
            sx={{
              backgroundColor: 'primary.main',
              '& .MuiCardHeader-title': { color: 'primary.contrastText' },
            }}
          />
          <CardContent>
            {jobseeker.references && jobseeker.references.length > 0 ? (
              <Grid container spacing={3}>
                {jobseeker.references.map((reference: JobseekerReference) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={reference.id}>
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {reference.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {reference.position} • {reference.relationship}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EmailIcon fontSize="small" color="primary" />
                        <Typography variant="body2">{reference.email}</Typography>
                      </Box>
                      {reference.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="primary" />
                          <Typography variant="body2">{formatPhone(reference.phone)}</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No reference information available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>
    </DashboardPageContainer>
  );
};

export default EmployerJobseekerView;
